import React, { useState, useEffect } from 'react'
import OrganizationForm from './manage-organisation'
import ManageJobFairs from './jobfairs/manage-jobfairs'
import { KTSVG } from '_metronic/helpers'
import { useAuth } from 'app/context/auth-context';
import { OrgService, Organization } from 'app/services/OrgService';
import { BoothService } from 'app/services/BoothService';
import { AlertModal } from 'app/modules/shared/modals/AlertModal';
import ManageUsers from './ManageUsers';
import { useAppContext } from 'app/context/app-context';
import ManageBooths from './booths/manage-booths';
import { Modal } from 'react-bootstrap-v5';
import { useHistory, useLocation } from 'react-router-dom';

const EmployerAdminDashboard: React.FC = () => {
  const history = useHistory();

  const [showFinishModal, setShowFinishModal] = useState<boolean>(false);
  const [showAdminNotAssignedModal, setShowAdminNotAssignedModal] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState(1);
  const { user } = useAuth();
  const [existingOrg, setExistingOrg] = useState<Organization | null>(null);
  const [showDetailsSavedModal, setShowDetailsSavedModal] = useState(false);
  const [steps, setSteps] = useState<{ title: string, description: string, enabled: boolean, completed: boolean }[]>(
    [
      { title: 'Organization', description: 'Setup organization details', enabled: true, completed: false },
      { title: 'Job Fairs', description: 'Job fair registration', enabled: false, completed: false },
      { title: 'Recruiters', description: 'Manage users', enabled: false, completed: false },
      { title: 'Booths', description: 'Manage Booth', enabled: false, completed: false },
    ]);

  const { jobFair } = useAppContext();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);

  // Add this state to track if we've handled the initial URL
  const [initialUrlHandled, setInitialUrlHandled] = useState(false);

  useEffect(() => {
    if (jobFair) {
      setSteps(prevSteps => {
        const updatedSteps = [...prevSteps];
        updatedSteps.find(x => x.title == "Booths")!.enabled = true;
        return updatedSteps;
      });
    }
  }, [jobFair]);


  // Replace the existing useEffect for URL handling
  useEffect(() => {

    const recursivelyEnableSteps = (stepNumber: number) => {
      const stepIndex = stepNumber - 1;
      if (stepNumber == 1) return;

      if (steps[stepIndex].enabled) {
        setCurrentStep(stepNumber);
      } else {
        recursivelyEnableSteps(stepNumber - 1);
      }
    };

    if (!initialUrlHandled) {
      const stepParam = searchParams.get('step');

      if (stepParam) {
        const stepNumber = parseInt(stepParam, 10);
        if (!isNaN(stepNumber) && stepNumber >= 1 && stepNumber <= steps.length) {
          recursivelyEnableSteps(stepNumber);
        }
        if (currentStep === stepNumber) {
          setInitialUrlHandled(true);
        }
      }
      else
        setInitialUrlHandled(true);
    }
  }, [searchParams, steps, initialUrlHandled]);

  const fetchOrgData = async () => {
    if (user?.id) {
      try {
        const orgService = new OrgService();
        const orgData = await orgService.getOrgById<Organization>(user.organizationId);
        if (orgData?.id) {
          setExistingOrg(orgData);
          setSteps(prevSteps => {
            const updatedSteps = [...prevSteps];
            updatedSteps[0].completed = true;
            updatedSteps[1].enabled = true;
            return updatedSteps;
          });
        }
      } catch (error) {
        console.error('Error fetching organization data:', error);
      }
    }
  };

  useEffect(() => {
    fetchOrgData();
  }, [user?.id]);

  // Update the step change functions
  const setStepQueryParam = (newStep: number) => {
    if (newStep >= 1 && newStep <= steps.length && steps[newStep - 1].enabled) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('step', newStep.toString());
      window.history.replaceState(null, '', `?${newSearchParams.toString()}`);
      setCurrentStep(newStep);
    }
  };

  // Update the prevStep and nextStep functions to also update the URL
  const prevStep = () => {
    if (currentStep > 1) {
      const newStep = currentStep - 1;
      setStepQueryParam(newStep);
    }
  };

  const proceedToNextStep = () => {
    if (currentStep < steps.length) {
      const newStep = currentStep + 1;
      setStepQueryParam(newStep);
    } else {
      setShowFinishModal(true);
    }
  };

  const orgFormRef = React.useRef<import('./manage-organisation').OrganizationFormHandle>(null);

  const nextStep = async () => {
    if (currentStep === 1) {
      if (orgFormRef.current) {
        if (orgFormRef.current.isDirty || !existingOrg) {
          await orgFormRef.current.submitForm();
        } else {
          proceedToNextStep();
        }
      }
    } else if (currentStep === steps.length) {
      if (user?.id && jobFair?.id) {
        try {
          const boothService = new BoothService();
          const boothList = await boothService.getAllBooths(jobFair.id);
          const isAssigned = boothList.some(booth => booth.recruiters?.includes(user.id));
          if (!isAssigned) {
            setShowAdminNotAssignedModal(true);
            return;
          }
        } catch (error) {
          console.error('Error checking booth assignment:', error);
        }
      }
      proceedToNextStep();
    } else {
      proceedToNextStep();
    }
  };

  const handleSponsorInvoicedCreate = () => {
    setSteps(prevSteps => {
      const updatedSteps = [...prevSteps];
      updatedSteps[1].completed = true;
      updatedSteps[2].enabled = true;
      return updatedSteps;
    });
    setCurrentStep(3);
    history.replace('/employer-admin/dashboard?step=3');
  };

  // Update handleStepClick to also update the URL
  const handleStepClick = (index: number) => {
    const step = steps[index];
    if (step.enabled) {
      const newStep = index + 1;
      setStepQueryParam(newStep);
    }
  };

  const SummaryModal = () => (
    <Modal
      show={showFinishModal}
      onHide={() => setShowFinishModal(false)}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Setup Complete!</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='alert alert-success'>
          <h4 className='mb-4'>You have successfully completed the setup!</h4>
        </div>

      </Modal.Body>
      <Modal.Footer>
        <button className='btn btn-primary' onClick={() => { setShowFinishModal(false); history.push('/recruiter/booth') }}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );

  return (
    <div className='stepper stepper-pills stepper-column d-flex flex-column flex-xl-row flex-row-fluid between m-10' id='kt_create_account_stepper'>
      <div className='card d-flex justify-content-center justify-content-xl-start flex-row-auto w-100 w-xl-250px w-xxl-300px me-9'>
        <div className='card-body px-6 px-lg-10 px-xxl-15 py-20'>
          <div className='stepper-nav'>
            {steps.map((step, index) => (
              <div key={index} className={`stepper-item ${currentStep === index + 1 ? 'current' : step.completed ? 'completed' : ''}`} onClick={() => handleStepClick(index)}
                style={{ cursor: step.enabled !== false ? 'pointer' : 'not-allowed' }}
              >
                <div className='stepper-wrapper d-flex'>
                  <div className="stepper-icon w-40px h-40px">
                    <i className="stepper-check fas fa-check"></i>
                    <span className="stepper-number">{index + 1}</span></div>
                  <div className='stepper-label'>
                    <h3 className='stepper-title'>{step.title} {step.enabled === false && <i className="stepper-check fas fa-lock"></i>}</h3>
                    <div className='stepper-desc fw-semibold'>{step.description}</div>
                  </div>
                </div>
                {index < steps.length - 1 && <div className='stepper-line h-40px'></div>}
              </div>

            ))}
          </div>
        </div>
      </div>

      <div className='d-flex flex-column flex-grow-1' style={{ minWidth: 0 }}>
        <div className='d-flex flex-stack flex-wrap p-3'>
          <button
            onClick={prevStep}
            type='button'
            className='btn btn-light-primary'
            disabled={currentStep === 1}
          >

            <KTSVG path='/media/icons/duotone/Navigation/Arrow-left.svg' className='svg-icon-4' />
            Back
          </button>
          <button disabled={currentStep !== 1 && currentStep !== steps.length && !steps[currentStep].enabled}
            onClick={nextStep}
            type='button'
            className='btn btn-primary'
          >
            {currentStep == steps.length ? 'Finish' : 'Next'}
            {currentStep !== steps.length && <KTSVG path='/media/icons/duotone/Navigation/Arrow-right.svg' className='svg-icon-3' />}
          </button>
        </div>
        {steps[currentStep - 1].title === "Organization" && <OrganizationForm ref={orgFormRef} existingOrg={existingOrg} sponsorId={user?.sponsorId} onSave={() => {
          setShowDetailsSavedModal(true);
          fetchOrgData();
        }} />}

        {steps[currentStep - 1].title === "Job Fairs" && <ManageJobFairs orgName={existingOrg?.companyName || ''} orgOnBoardingProgressPercentage={existingOrg?.onBoardingProgress?.progress || 0} onSponsorInvoicedCreate={handleSponsorInvoicedCreate} onLoad={(isLoaded: boolean) => {
          if (isLoaded) {
            setSteps(prevSteps => {
              const updatedSteps = [...prevSteps];
              updatedSteps[1].completed = true;
              updatedSteps[2].enabled = isLoaded;
              return updatedSteps;
            });
          }
        }} />}

        {steps[currentStep - 1].title === "Recruiters" && existingOrg?.id && <ManageUsers organizationId={existingOrg.id} />}

        {steps[currentStep - 1].title === "Booths" && existingOrg?.id && <ManageBooths orgOnBoardingProgressPercentage={existingOrg.onBoardingProgress.progress} orgName={existingOrg.companyName || ''} orgLogo={existingOrg.companyLogo} orgJobBoardUrl={existingOrg.jobBoardLink} onLoad={(isLoaded: boolean) => {
          if (isLoaded) {
            setSteps(prevSteps => {
              const updatedSteps = [...prevSteps];
              updatedSteps[2].completed = true;
              return updatedSteps;
            });
          }
        }} />}


      </div>
      <AlertModal
        show={showDetailsSavedModal}
        onOK={() => { setShowDetailsSavedModal(false); proceedToNextStep(); }}
        title="Success"
      >
        Details Saved Successfully </AlertModal>
      <AlertModal
        show={showAdminNotAssignedModal}
        onOK={() => setShowAdminNotAssignedModal(false)}
        title="Mandatory Assignment"
      >
        It is mandatory to assign yourself to a booth and then setup can be completed.
      </AlertModal>
      <SummaryModal />
    </div>
  )
}

export default EmployerAdminDashboard
